import {useCallback, useRef} from 'react';
import type {SwiperClass} from 'swiper/react';
import {useProductGallery} from '../../../../ProductGalleryProvider/ProductGalleryProvider';
import {ArrowDirection, SWIPER_SPEED} from '../constants';
import {useEnvironment} from '@wix/yoshi-flow-editor';

export const useSwiperNavigation = (mediaLength: number, swiper: SwiperClass, isVertical: boolean) => {
  const {selectedIndex, changeSelectedIndex} = useProductGallery();
  const {isRTL} = useEnvironment();
  const navigationLockRef = useRef(false);

  const unlockNavigation = () => {
    navigationLockRef.current = false;
  };

  const blockNavigation = useCallback(() => {
    navigationLockRef.current = true;
    setTimeout(unlockNavigation, SWIPER_SPEED);
  }, []);

  const navigate = useCallback(
    (isPrev: boolean) => {
      const isNavigationBlocked = navigationLockRef.current;
      if (isNavigationBlocked) {
        return;
      }
      let newIndex = selectedIndex;
      if (isPrev) {
        newIndex = selectedIndex === 0 ? mediaLength - 1 : selectedIndex - 1;
      } else {
        newIndex = selectedIndex === mediaLength - 1 ? 0 : selectedIndex + 1;
      }
      swiper?.slideToLoop(newIndex);
      changeSelectedIndex(newIndex);
      blockNavigation();
    },
    [selectedIndex, mediaLength, changeSelectedIndex, blockNavigation, swiper]
  );

  const prevNavigation = useCallback(() => navigate(true), [navigate]);
  const nextNavigation = useCallback(() => navigate(false), [navigate]);

  const handleKeyPress = useCallback(
    (keyCode: number) => {
      if (isVertical) {
        if (keyCode === ArrowDirection.UP) {
          prevNavigation();
          return;
        }
        if (keyCode === ArrowDirection.DOWN) {
          nextNavigation();
        }
        return;
      }
      if (keyCode === ArrowDirection.LEFT) {
        isRTL ? nextNavigation() : prevNavigation();
        return;
      }
      if (keyCode === ArrowDirection.RIGHT) {
        isRTL ? prevNavigation() : nextNavigation();
      }
    },
    [isVertical, isRTL, prevNavigation, nextNavigation]
  );

  return {
    prevNavigation,
    nextNavigation,
    handleKeyPress,
  };
};
